import { motion, AnimatePresence } from "framer-motion";
import {
  Zap,
  BrainCircuit,
  Share2,
  User,
  Building2,
  Mail,
} from "lucide-react";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { NavLink } from "react-router";
import logoImage from "./assets/logo.png";
import neuroVideo from "./assets/neuro_vid.mp4";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";
import * as api from "./api";
import { isValidUser } from "./authConfig";

// --- Utility ---
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// --- Constants ---
const SECTIONS = ["Welcome", "Features", "Process", "Team", "Contact"];

// --- Components ---

const Navbar = ({ currentSlide, isLoggedIn, onNavigate }) => {
  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50 px-6 py-6 pointer-events-none text-slate-900"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between pointer-events-auto">
        <div className="flex items-center gap-3">
          <img
            src={logoImage}
            alt="neurohubs"
            className="h-8 w-8 object-contain"
          />
          <span className="text-lg font-semibold tracking-tight text-blue-600">
            Neurohubs
          </span>
        </div>

        <div className="hidden md:flex items-center gap-12">
          {SECTIONS.map((item, idx) => (
            <button
              key={item}
              type="button"
              onClick={() => onNavigate?.(idx)}
              className="relative group cursor-pointer"
            >
              <span
                className={cn(
                  "text-sm font-medium transition-opacity duration-300",
                  currentSlide === idx
                    ? "opacity-100"
                    : "opacity-40 hover:opacity-80"
                )}
              >
                {item}
              </span>
              {currentSlide === idx && (
                <motion.div
                  layoutId="nav-indicator"
                  className="absolute -bottom-2 left-0 right-0 h-px bg-slate-900"
                />
              )}
            </button>
          ))}
        </div>

        <NavLink
          to={isLoggedIn ? "/app" : "/login"}
          className="flex items-center gap-2 text-xs md:text-sm font-medium border border-slate-200 px-4 py-2 md:px-6 md:py-2.5 rounded-full hover:bg-slate-900 hover:text-white transition-all"
        >
          {isLoggedIn ? "Open App" : "Login"}
        </NavLink>
      </div>
    </motion.nav>
  );
};

const SlideContainer = ({ children, isMobile, innerRef }) => {
  return (
    <div
      className={cn(
        "w-full relative flex items-center justify-center overflow-hidden",
        isMobile ? "min-h-screen pt-24 pb-20 px-4" : "h-screen pt-20"
      )}
      ref={innerRef}
    >
      {children}
    </div>
  );
};

// --- Slides ---

const HeroSlide = () => {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center relative overflow-hidden bg-[#FDFDFD] px-4 sm:px-6">
      {/* Side Scroll Indicators */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="absolute left-6 md:left-10 top-1/2 -translate-y-1/2 hidden md:block select-none"
      >
        <span
          className="block text-[10px] tracking-[0.4em] text-gray-300 font-semibold"
          style={{ writingMode: "vertical-rl" }}
        >
          SCROLL DOWN
        </span>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="absolute right-6 md:right-10 top-1/2 -translate-y-1/2 hidden md:block select-none"
      >
        <span
          className="block text-[10px] tracking-[0.4em] text-gray-300 font-semibold"
          style={{ writingMode: "vertical-rl" }}
        >
          SCROLL DOWN
        </span>
      </motion.div>

      <div className="relative z-10 text-center max-w-6xl mx-auto px-6">
        {/* Primary identity - what we are */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-6 md:mb-8"
        >
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-semibold tracking-tight text-[#0B1120]">
            Digital Analytics Center
            <span className="block text-blue-600">for qEEG</span>
          </h1>
        </motion.div>

        {/* Visual separator */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="w-16 md:w-24 h-px bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200 mx-auto mb-6 md:mb-8"
        />

        {/* Marketing statement - the vision */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-light text-gray-600 font-display leading-relaxed"
        >
          The future of{" "}
          <span className="text-gray-800 font-normal">brain signals</span>{" "}
          is here.
        </motion.p>
      </div>
    </div>
  );
};

const ProcessSlide = () => {
  const [activeStep, setActiveStep] = useState(null);
  const [hoveredStep, setHoveredStep] = useState(null);

  const steps = [
    {
      id: 1,
      label: "Upload",
      desc: "You just upload structured qEEG data.",
    },
    {
      id: 2,
      label: "Extract",
      desc: "Neurohubs automatically extracts all reported power values from the clinical report and maps them to the electrode montage.",
    },
    {
      id: 3,
      label: "Analyze",
      desc: "Neurohubs analyzes qEEG power metrics and ratios relative to age-matched norms.",
    },
    {
      id: 4,
      label: "Report",
      desc: "Neurohubs produces an interactive heatmap report showing deviations from normative ranges, organized by diagnostic themes and interpretations for clinical review.",
    },
  ];

  // Determine which label to show based on hovered step
  const getHoverLabel = () => {
    if (hoveredStep === null) return null;
    return hoveredStep === 0 ? "YOU" : "NEUROHUBS";
  };

  const hoverLabel = getHoverLabel();
  const activeStepDesc = activeStep !== null ? steps[activeStep]?.desc : null;

  return (
    <div className="w-full h-full flex items-center bg-white relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 w-full relative z-10">
        <div className="text-center mb-8 md:mb-12 max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#0B1120] mb-4 md:mb-6"
          >
            From signal to <span className="text-blue-600">insight</span>.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-base sm:text-lg md:text-xl text-gray-500 font-light leading-relaxed"
          >
            Automated qEEG analysis grounded in medical knowledge.
            <br className="hidden md:block" />
            Reliable, repeatable, clinically trustworthy.
          </motion.p>
        </div>

        {/* Role indicator (hover) */}
        <div className="text-center mb-6 md:mb-10 min-h-[40px] flex items-center justify-center px-2">
          <AnimatePresence mode="wait">
            {hoverLabel ? (
              <motion.div
                key={hoverLabel}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                className="flex items-center gap-3"
              >
                <span className="text-[10px] md:text-xs tracking-[0.3em] text-gray-400 uppercase">
                  Handled by
                </span>
                <span
                  className={cn(
                    "text-base md:text-lg font-semibold tracking-tight",
                    hoveredStep === 0 ? "text-gray-800" : "text-blue-500"
                  )}
                >
                  {hoverLabel}
                </span>
              </motion.div>
            ) : (
              <motion.span
                key="role-helper"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="text-[13px] md:text-sm text-gray-400 font-light"
              >
                Tap a step to see who handles it and click to see what happens.
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 relative mt-2 md:mt-4">
          {/* Connecting Line */}
          <div className="absolute top-[5rem] left-0 right-0 h-px bg-gray-100 -z-10 hidden md:block" />

          {steps.map((item, i) => {
            const isActive = activeStep === i;

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="relative flex flex-col items-center"
                onMouseEnter={() => setHoveredStep(i)}
                onMouseLeave={() => setHoveredStep(null)}
              >
                <div
                  onClick={() => setActiveStep(isActive ? null : i)}
                  className={cn(
                    "cursor-pointer transition-all duration-300 group relative z-10",
                    isActive ? "scale-105" : "hover:scale-105"
                  )}
                >
                  <div
                    className={cn(
                      "w-28 h-40 md:w-32 md:h-48 rounded-[100px] border flex flex-col items-center justify-center mb-6 mx-auto bg-white transition-all duration-500 shadow-sm relative overflow-hidden",
                      isActive
                        ? "border-blue-500 shadow-lg shadow-blue-500/20 bg-blue-50"
                        : "border-gray-100 hover:border-blue-200 hover:shadow-md"
                    )}
                  >
                    <span
                      className={cn(
                        "text-4xl md:text-6xl font-light font-display transition-colors duration-300 mb-1 md:mb-2",
                        isActive
                          ? "text-blue-600"
                          : "text-gray-200 group-hover:text-blue-400"
                      )}
                    >
                      {item.id}
                    </span>
                    <span
                      className={cn(
                        "text-xs md:text-sm font-bold tracking-wide uppercase",
                        isActive
                          ? "text-blue-900"
                          : "text-gray-400 group-hover:text-gray-600"
                      )}
                    >
                      {item.label}
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Click description (below tiles) */}
        <div className="text-center mt-8 md:mt-12 min-h-[72px] flex items-center justify-center px-2">
          <AnimatePresence mode="wait">
            {activeStepDesc ? (
              <motion.p
                key={`active-step-desc-${activeStep}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                className="max-w-3xl text-sm sm:text-base md:text-lg font-medium text-gray-700 leading-relaxed"
              >
                {activeStepDesc}
              </motion.p>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

const FeaturesSlide = () => {
  const features = [
    { title: "Auto analysis", desc: "Review-ready interpretation." },
    { title: "Universal data input", desc: "Standard clinical format." },
    { title: "System integration", desc: "Works with existing clinical systems." },
  ];

  return (
    <div className="w-full h-full flex items-center bg-[#F8FAFC]">
      <div className="max-w-[1400px] w-full mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-20 items-center">
        <div className="order-2 md:order-1 relative">
          {/* Video Showcase */}
          <div className="aspect-[4/3] md:aspect-square relative rounded-[2rem] md:rounded-[3rem] overflow-hidden bg-white shadow-2xl shadow-blue-900/5 flex items-center justify-center max-h-[420px]">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-white" />
            <div className="relative z-10 w-full h-full flex items-center justify-center p-4 sm:p-6 md:p-8">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-contain rounded-2xl"
              >
                <source src={neuroVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>

        <div className="order-1 md:order-2">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#0B1120] mb-8 md:mb-12">
              Complex before. <br />
              <span className="text-blue-600"> Simple now.</span>
            </h2>

            <div className="space-y-8 md:space-y-12">
              {features.map((f, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-4 md:gap-6 group"
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-500">
                    {i === 0 && <Zap className="w-5 h-5" />}
                    {i === 1 && <BrainCircuit className="w-5 h-5" />}
                    {i === 2 && <Share2 className="w-5 h-5" />}
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-medium text-[#0B1120] mb-1">
                      {f.title}
                    </h3>
                    <p className="text-gray-500 font-light text-sm sm:text-base">{f.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const TeamSlide = () => {
  const team = [
    {
      name: "dr Piotr Orchowski",
      title: "medical advisor, neurology",
      linkUrl: "https://fundacjaorchidea.pl/",
      linkLabel: "Fundacja Orchidea",
      imageUrl: "/team/piotr.png",
      imageWebp: "/team/piotr.webp",
    },
    {
      name: "Mateusz Stachowicz",
      title: "computer science",
      linkUrl: "https://www.linkedin.com/in/mtstch/",
      linkLabel: "LinkedIn",
      imageUrl: "/team/mateusz.png",
      imageWebp: "/team/mateusz.webp",
    },
    {
      name: "Anna Buchwald",
      title: "neurotechnology",
      linkUrl: "https://www.linkedin.com/in/annabuchwald/",
      linkLabel: "LinkedIn",
      imageUrl: "/team/anna.png",
      imageWebp: "/team/anna.webp",
    },
  ];

  return (
    <div className="w-full h-full flex items-center bg-white relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 w-full">
        <div className="text-center mb-10 md:mb-16">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-xs tracking-[0.3em] text-gray-400 uppercase"
          >
            Mission
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#0B1120] mt-3"
          >
            Faster path to <span className="text-blue-600">healthy brains</span>.
          </motion.h2>
        </div>

        <div className="text-center mb-10">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-base sm:text-lg md:text-xl text-gray-500 font-light"
          >
            Team behind the mission
          </motion.p>
        </div>

        <div className="grid grid-cols-3 items-stretch gap-2.5 sm:gap-3 md:gap-6 w-full">
          {team.map((member, i) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="bg-white border border-gray-100 rounded-lg p-2.5 sm:p-3.5 md:p-6 shadow-sm h-full"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 sm:w-18 sm:h-18 md:w-28 md:h-28 rounded-full overflow-hidden bg-white mb-3 md:mb-5 ring-1 ring-gray-200">
                  <picture>
                    <source srcSet={member.imageWebp} type="image/webp" />
                    <source srcSet={member.imageUrl} type="image/png" />
                    <img
                      src={member.imageUrl}
                      alt={member.name}
                      className="w-full h-full object-cover object-center"
                      loading="lazy"
                      decoding="async"
                      width="112"
                      height="112"
                      sizes="112px"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </picture>
                </div>

                <div className="text-xs sm:text-sm md:text-lg font-semibold tracking-tight text-[#0B1120] leading-tight">
                  {member.name}
                </div>
                <div className="text-[11px] sm:text-xs md:text-sm text-gray-500 font-light mt-1 text-center leading-tight">
                  {member.title}
                </div>

                <a
                  href={member.linkUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 md:mt-4 text-[11px] sm:text-xs md:text-sm font-medium text-blue-600 hover:text-[#0B1120] transition-colors"
                >
                  {member.linkLabel}
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ContactSlide = () => {
  // Form state
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    company: "",
  });

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(""); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const response = await api.submitDemoRequest(formData);

      if (response.status === 200) {
        setIsSubmitted(true);

        // Reset form after 5 seconds
        setTimeout(() => {
          setFormData({ full_name: "", email: "", company: "" });
          setIsSubmitted(false);
        }, 5000);
      }
    } catch (err) {
      console.error("Demo request error:", err);
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError("Failed to submit request. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full h-full flex items-center justify-center bg-white relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-50 via-white to-white" />

      <div className="max-w-xl w-full mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tighter text-[#0B1120] mb-4">
            Request a demo
          </h2>
          <p className="text-lg text-gray-500">
            See how <span className="font-semibold text-blue-600">Neurohubs</span> can transform your diagnostic workflow.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-6 md:p-8 shadow-2xl shadow-blue-900/5 border border-gray-100"
        >
          {/* Success Message */}
          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Thank you!
              </h3>
              <p className="text-gray-500">
                We've received your request and will be in touch soon.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Full Name */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700 pl-1">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3.5 top-3.5 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    placeholder="Dr. John Doe"
                    required
                    disabled={isSubmitting}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-11 pr-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Work Email */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700 pl-1">
                  Work Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-3.5 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@hospital.org"
                    required
                    disabled={isSubmitting}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-11 pr-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Company */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700 pl-1">
                  Company or Institution
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3.5 top-3.5 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="General Hospital"
                    required
                    disabled={isSubmitting}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-11 pr-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm"
                >
                  {error}
                </motion.div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#0B1120] text-white py-4 rounded-xl font-medium hover:bg-blue-600 transition-colors shadow-lg shadow-blue-900/20 mt-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Submitting...
                  </>
                ) : (
                  "Submit Request"
                )}
              </button>
            </form>
          )}
        </motion.div>

        <div className="mt-12 text-center text-xs text-gray-400 flex items-center justify-center gap-6">
          <span>© 2026 Neurohubs</span>
          <NavLink to="/privacy" className="hover:text-gray-900 transition-colors">
            Privacy Policy
          </NavLink>
          <NavLink to="/terms" className="hover:text-gray-900 transition-colors">
            Terms of Service
          </NavLink>
          <a
            href="https://jointhubs.com/"
            target="_blank"
            rel="noreferrer"
            className="hover:text-gray-900 transition-colors"
          >
            jointhubs.com
          </a>
        </div>
      </div>
    </div>
  );
};

// --- Main Layout ---

const Landing = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const totalSlides = SECTIONS.length;
  const touchStart = useRef(0);
  const slideRefs = useRef([]);

  useEffect(() => {
    const savedUser = JSON.parse(localStorage.getItem("user"));
    setIsLoggedIn(isValidUser(savedUser));
  }, []);

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const handleMediaChange = (event) => setIsMobile(event.matches);

    setIsMobile(mql.matches);
    mql.addEventListener("change", handleMediaChange);

    return () => mql.removeEventListener("change", handleMediaChange);
  }, []);

  useEffect(() => {
    const htmlEl = document.documentElement;
    const bodyEl = document.body;
    const previousHtmlOverflow = htmlEl.style.overflow;
    const previousBodyOverflow = bodyEl.style.overflow;

    if (isMobile) {
      htmlEl.style.overflowX = "hidden";
      htmlEl.style.overflowY = "auto";
      bodyEl.style.overflowX = "hidden";
      bodyEl.style.overflowY = "auto";
    } else {
      htmlEl.style.overflow = "hidden";
      bodyEl.style.overflow = "hidden";
    }

    return () => {
      htmlEl.style.overflow = previousHtmlOverflow;
      bodyEl.style.overflow = previousBodyOverflow;
    };
  }, [isMobile]);

  // Custom scroll jacking
  const handleWheel = useCallback(
    (e) => {
      if (isMobile) return;
      if (isScrolling) return;

      if (Math.abs(e.deltaY) > 20) {
        setIsScrolling(true);
        const direction = e.deltaY > 0 ? 1 : -1;

        setCurrentSlide((prev) => {
          const next = prev + direction;
          if (next >= 0 && next < totalSlides) {
            return next;
          }
          return prev;
        });

        setTimeout(() => {
          setIsScrolling(false);
        }, 1000);
      }
    },
    [isMobile, isScrolling, totalSlides]
  );

  useEffect(() => {
    if (isMobile) return undefined;
    window.addEventListener("wheel", handleWheel, { passive: true });
    return () => window.removeEventListener("wheel", handleWheel);
  }, [handleWheel, isMobile]);

  // Touch support
  const handleTouchStart = (e) => {
    if (isMobile) return;
    touchStart.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (isMobile) return;
    if (isScrolling) return;
    const touchEnd = e.changedTouches[0].clientY;
    const delta = touchStart.current - touchEnd;

    if (Math.abs(delta) > 50) {
      setIsScrolling(true);
      const direction = delta > 0 ? 1 : -1;
      setCurrentSlide((prev) => {
        const next = prev + direction;
        if (next >= 0 && next < totalSlides) return next;
        return prev;
      });
      setTimeout(() => setIsScrolling(false), 1000);
    }
  };

  const slides = [
    HeroSlide,
    FeaturesSlide,
    ProcessSlide,
    TeamSlide,
    ContactSlide,
  ];

  useEffect(() => {
    if (!isMobile) return;
    const target = slideRefs.current[currentSlide];
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [currentSlide, isMobile]);

  return (
    <div
      className={cn(
        "w-full bg-white text-[#0B1120] font-sans touch-pan-y",
        isMobile
          ? "relative min-h-screen overflow-x-hidden overflow-y-auto"
          : "h-screen overflow-hidden fixed inset-0"
      )}
      onTouchStart={isMobile ? undefined : handleTouchStart}
      onTouchEnd={isMobile ? undefined : handleTouchEnd}
    >
      <Navbar
        currentSlide={currentSlide}
        isLoggedIn={isLoggedIn}
        onNavigate={(idx) => setCurrentSlide(idx)}
      />

      {isMobile ? (
        <div className="flex flex-col">
          {slides.map((Slide, idx) => (
            <SlideContainer
              key={Slide.name}
              isMobile
              innerRef={(el) => {
                slideRefs.current[idx] = el;
              }}
            >
              <Slide />
            </SlideContainer>
          ))}
        </div>
      ) : (
        <motion.div
          animate={{ y: `-${currentSlide * 100}%` }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full h-full"
        >
          {slides.map((Slide) => (
            <SlideContainer key={Slide.name}>
              <Slide />
            </SlideContainer>
          ))}
        </motion.div>
      )}

      {isMobile ? (
        <div className="fixed inset-x-0 bottom-4 z-50 flex items-center justify-center gap-3">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={cn(
                "w-3 h-3 rounded-full transition-all duration-300 border border-white/60 bg-white/80 shadow",
                currentSlide === i ? "bg-blue-600 border-blue-600 scale-110" : ""
              )}
            />
          ))}
        </div>
      ) : (
        <div className="fixed right-6 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-4">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-300",
                currentSlide === i
                  ? "bg-blue-600 scale-125"
                  : "bg-gray-300 hover:bg-gray-400"
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Landing;
